import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, auth } from "../lib/api";
import { toast } from "sonner";
import BrandHeader from "../components/BrandHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2 } from "lucide-react";

export default function AdminLogin() {
  const nav = useNavigate();
  const [mode, setMode] = useState("login"); // 'login' | 'request'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/login", { email: email.trim(), password });
      auth.setSession(r.data.access_token, r.data.email);
      toast.success("Signed in");
      nav("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign in failed");
    } finally {
      setBusy(false);
    }
  };

  const doRequestLink = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/request-setup-link", { email: email.trim() });
      setSent(true);
    } catch (err) {
      // Endpoint is deliberately silent; only network errors surface here
      toast.error("Couldn't send the link. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <BrandHeader
        right={
          <Link
            to="/"
            className="text-xs text-neutral-500 hover:text-neutral-800"
            data-testid="back-to-form-link"
          >
            Teacher form
          </Link>
        }
      />
      <main className="max-w-md mx-auto px-4 sm:px-6 py-10">
        <div className="rounded-xl bg-white border border-neutral-200 p-6 sm:p-8">
          <h1 className="font-display text-2xl font-semibold text-neutral-900 mb-1">
            Admin sign in
          </h1>
          <p className="text-sm text-neutral-500 mb-6">
            Single-admin access. No accounts to create — if this is your first time,
            request a password-setup link below.
          </p>

          {mode === "login" ? (
            <form onSubmit={doLogin} className="space-y-4" data-testid="admin-login-form">
              <div>
                <Label className="text-sm text-neutral-700 mb-1 block">Email</Label>
                <Input
                  type="email"
                  data-testid="login-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11"
                  placeholder="you@letssiva.com"
                  autoComplete="email"
                />
              </div>
              <div>
                <Label className="text-sm text-neutral-700 mb-1 block">Password</Label>
                <Input
                  type="password"
                  data-testid="login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-11"
                  autoComplete="current-password"
                />
              </div>
              <Button
                type="submit"
                disabled={busy}
                data-testid="btn-login"
                className="h-11 w-full bg-[#2D5A4F] hover:bg-[#264d43] text-white"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
              </Button>
              <button
                type="button"
                onClick={() => { setMode("request"); setSent(false); }}
                className="text-xs text-neutral-500 hover:text-neutral-800 w-full text-center pt-2"
                data-testid="link-request-setup"
              >
                First time here, or forgot password? Request a setup link
              </button>
            </form>
          ) : (
            <form onSubmit={doRequestLink} className="space-y-4" data-testid="admin-request-link-form">
              {sent ? (
                <div
                  data-testid="link-sent-banner"
                  className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900"
                >
                  If that email is the registered admin, a setup link has been sent.
                  Check your inbox (and spam folder). The link expires in 24 hours.
                </div>
              ) : (
                <div>
                  <Label className="text-sm text-neutral-700 mb-1 block">Admin email</Label>
                  <Input
                    type="email"
                    data-testid="request-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-11"
                    placeholder="you@letssiva.com"
                    autoComplete="email"
                  />
                </div>
              )}
              {!sent && (
                <Button
                  type="submit"
                  disabled={busy}
                  data-testid="btn-send-setup-link"
                  className="h-11 w-full bg-[#2D5A4F] hover:bg-[#264d43] text-white"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Email me a setup link"}
                </Button>
              )}
              <button
                type="button"
                onClick={() => { setMode("login"); setSent(false); }}
                className="text-xs text-neutral-500 hover:text-neutral-800 w-full text-center pt-2"
                data-testid="link-back-to-login"
              >
                Back to sign in
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
